import { IonContent, IonLoading, IonPage, useIonToast, useIonViewWillEnter } from '@ionic/react';
import { MenuItem, Select } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import axios from '../../axios';
import Header from '../../components/Header';
import { createDocumentFields } from '../../models/createDocumentFields';

const CreateDocument: React.FC = () => {
  const router = useHistory();
  const [present] = useIonToast();
  const { t } = useTranslation();

  const [showLoading, setShowLoading] = useState(false);
  const [data, setData] = useState<any>(createDocumentFields);

  const [categorySearch, setCategorySearch] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [categoryPage, setCategoryPage] = useState(0);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const categoryScrollRef = React.useRef<HTMLDivElement>(null);

  const [typeSearch, setTypeSearch] = useState('');
  const [typeOptions, setTypeOptions] = useState<any[]>([]);
  const [typePage, setTypePage] = useState(0);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [loadingType, setLoadingType] = useState(false);
  const typeScrollRef = React.useRef<HTMLDivElement>(null);

  const [assetSearch, setAssetSearch] = useState('');
  const [assetOptions, setAssetOptions] = useState<any[]>([]);
  const [assetPage, setAssetPage] = useState(0);
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [loadingAsset, setLoadingAsset] = useState(false);
  const assetScrollRef = React.useRef<HTMLDivElement>(null);

  const [projectSearch, setProjectSearch] = useState('');
  const [projectOptions, setProjectOptions] = useState<any[]>([]);
  const [projectPage, setProjectPage] = useState(0);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const projectScrollRef = React.useRef<HTMLDivElement>(null);

  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [userPage, setUserPage] = useState(0);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const userScrollRef = React.useRef<HTMLDivElement>(null);

  useIonViewWillEnter(async () => {
    try {
      const getBasicAuth = () => `Basic ${localStorage.getItem('hyprism_login')}`;
      const [resCat, resType, resProj, resAsset, resUser] = await Promise.all([
        axios({ method: 'get', url: `/resource/Compliance document category?limit_page_length=500`, headers: { Authorization: getBasicAuth() } }),
        axios({ method: 'get', url: `/resource/Compliance document type?limit_page_length=500`, headers: { Authorization: getBasicAuth() } }),
        axios({ method: 'get', url: `/resource/Project?limit_page_length=500`, headers: { Authorization: getBasicAuth() } }),
        axios({ method: 'get', url: `/resource/Asset?limit_page_length=500`, headers: { Authorization: getBasicAuth() } }),
        axios({ method: 'get', url: `/resource/User?limit_page_length=500&filters=[["enabled","=",1]]`, headers: { Authorization: getBasicAuth() } }),
      ]);

      const catOptions = resCat?.data?.data?.map((item: any) => item.name) || [];
      const typeOptions = resType?.data?.data?.map((item: any) => item.name) || [];
      const projOptions = resProj?.data?.data?.map((item: any) => item.project_name || item.name) || [];
      const assetOptions = resAsset?.data?.data?.map((item: any) => item.name) || [];
      const userOptions = resUser?.data?.data?.map((item: any) => item.name) || [];

      setData((prevData: any) => {
        const newData = [...prevData];
        const updateOpt = (f: string, opts: any[]) => {
          const idx = newData.findIndex((d: any) => d.field === f);
          if (idx >= 0) newData[idx].options = opts;
        };
        updateOpt('document_category', catOptions);
        updateOpt('document_type', typeOptions);
        updateOpt('assigned_projects_input', projOptions);
        updateOpt('assigned_assets_input', assetOptions);
        updateOpt('assigned_users_input', userOptions);

        return newData;
      });
    } catch (err) {
      console.log('Error pre-fetching document select types', err);
    }
  });

  const fetchCategories = async (search: string, page: number, append = false) => {
    setLoadingCategory(true);
    try {
      const config: any = {
        method: 'get',
        url: `/resource/Compliance document category?filters=[["name","like","%${search}%"]]&limit_page_length=20&limit_start=${page * 20}`,
        headers: {
          Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
        },
      };
      const res = await axios(config);
      const newOptions = res?.data?.data || [];
      if (append) {
        setCategoryOptions((prev) => [...prev, ...newOptions]);
      } else {
        setCategoryOptions(newOptions);
      }
    } catch (e) {
      console.log('Error fetching categories', e);
    } finally {
      setLoadingCategory(false);
    }
  };

  const handleCategorySearchChange = (e: any) => {
    // Disabled search via typing
  };

  const handleCategoryScroll = (e: any) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && !loadingCategory) {
      const nextPage = categoryPage + 1;
      setCategoryPage(nextPage);
      fetchCategories(categorySearch, nextPage, true);
    }
  };

  const selectCategory = (category: any) => {
    updateField('document_category', category.name);
    setCategorySearch(category.name);
    setShowCategoryDropdown(false);
  };

  const fetchTypes = async (search: string, page: number, append = false) => {
    setLoadingType(true);
    try {
      const config: any = {
        method: 'get',
        url: `/resource/Compliance document type?filters=[["name","like","%${search}%"]]&limit_page_length=20&limit_start=${page * 20}`,
        headers: {
          Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
        },
      };
      const res = await axios(config);
      const newOptions = res?.data?.data || [];
      if (append) {
        setTypeOptions((prev) => [...prev, ...newOptions]);
      } else {
        setTypeOptions(newOptions);
      }
    } catch (e) {
      console.log('Error fetching types', e);
    } finally {
      setLoadingType(false);
    }
  };

  const handleTypeSearchChange = (e: any) => {
    // Disabled search via typing
  };

  const handleTypeScroll = (e: any) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && !loadingType) {
      const nextPage = typePage + 1;
      setTypePage(nextPage);
      fetchTypes(typeSearch, nextPage, true);
    }
  };

  const selectType = (docType: any) => {
    updateField('document_type', docType.name);
    setTypeSearch(docType.name);
    setShowTypeDropdown(false);
  };

  const fetchAssets = async (search: string, page: number, append = false) => {
    setLoadingAsset(true);
    try {
      const config: any = {
        method: 'get',
        url: `/resource/Asset?filters=[["name","like","%${search}%"]]&limit_page_length=20&limit_start=${page * 20}`,
        headers: {
          Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
        },
      };
      const res = await axios(config);
      const newOptions = res?.data?.data || [];
      if (append) {
        setAssetOptions((prev) => [...prev, ...newOptions]);
      } else {
        setAssetOptions(newOptions);
      }
    } catch (e) {
      console.log('Error fetching assets', e);
    } finally {
      setLoadingAsset(false);
    }
  };

  const handleAssetSearchChange = (e: any) => {
    const newVal = e.target.value;
    setAssetSearch(newVal);
    setAssetPage(0);
    setShowAssetDropdown(true);
    fetchAssets(newVal, 0, false);
  };

  const handleAssetScroll = (e: any) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && !loadingAsset) {
      const nextPage = assetPage + 1;
      setAssetPage(nextPage);
      fetchAssets(assetSearch, nextPage, true);
    }
  };

  const selectAsset = (asset: any) => {
    const fieldIndex = data.findIndex((v: any) => v.field === 'assigned_assets_input');
    const currentVal = data[fieldIndex].value || '';
    const arr = currentVal ? currentVal.split(',').map((a: string) => a.trim()).filter((a: string) => a) : [];
    if (!arr.includes(asset.name)) {
      arr.push(asset.name);
      updateField('assigned_assets_input', arr.join(', '));
    }
    setAssetSearch('');
    setShowAssetDropdown(false);
  };

  const removeAssetFromField = (assetName: string) => {
    const fieldIndex = data.findIndex((v: any) => v.field === 'assigned_assets_input');
    if (fieldIndex >= 0) {
      const currentVal = data[fieldIndex].value || '';
      const arr = currentVal.split(',').map((a: string) => a.trim()).filter((a: string) => a && a !== assetName);
      updateField('assigned_assets_input', arr.join(', '));
    }
  };

  const fetchProjects = async (search: string, page: number, append = false) => {
    setLoadingProject(true);
    try {
      const config: any = {
        method: 'get',
        url: `/resource/Project?filters=[["project_name","like","%${search}%"]]&limit_page_length=20&limit_start=${page * 20}`,
        headers: {
          Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
        },
      };
      const res = await axios(config);
      const newOptions = res?.data?.data || [];
      if (append) {
        setProjectOptions((prev) => [...prev, ...newOptions]);
      } else {
        setProjectOptions(newOptions);
      }
    } catch (e) {
      console.log('Error fetching projects', e);
    } finally {
      setLoadingProject(false);
    }
  };

  const handleProjectSearchChange = (e: any) => {
    // Disabled search via typing
  };

  const handleProjectScroll = (e: any) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && !loadingProject) {
      const nextPage = projectPage + 1;
      setProjectPage(nextPage);
      fetchProjects(projectSearch, nextPage, true);
    }
  };

  const selectProject = (project: any) => {
    updateField('assigned_projects_input', project.project_name || project.name);
    setProjectSearch(project.project_name || project.name);
    setShowProjectDropdown(false);
  };

  const fetchUsers = async (search: string, page: number, append = false) => {
    setLoadingUser(true);
    try {
      const config: any = {
        method: 'get',
        url: `/resource/User?filters=[["enabled","=",1],["name","like","%${search}%"]]&limit_page_length=20&limit_start=${page * 20}`,
        headers: {
          Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
        },
      };
      const res = await axios(config);
      const newOptions = res?.data?.data || [];
      if (append) {
        setUserOptions((prev) => [...prev, ...newOptions]);
      } else {
        setUserOptions(newOptions);
      }
    } catch (e) {
      console.log('Error fetching users', e);
    } finally {
      setLoadingUser(false);
    }
  };

  const handleUserSearchChange = (e: any) => {
    const newVal = e.target.value;
    setUserSearch(newVal);
    setUserPage(0);
    setShowUserDropdown(true);
    fetchUsers(newVal, 0, false);
  };

  const handleUserScroll = (e: any) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && !loadingUser) {
      const nextPage = userPage + 1;
      setUserPage(nextPage);
      fetchUsers(userSearch, nextPage, true);
    }
  };

  const selectUser = (user: any) => {
    const fieldIndex = data.findIndex((v: any) => v.field === 'assigned_users_input');
    const currentVal = data[fieldIndex].value || '';
    const arr = currentVal ? currentVal.split(',').map((a: string) => a.trim()).filter((a: string) => a) : [];
    if (!arr.includes(user.name)) {
      arr.push(user.name);
      updateField('assigned_users_input', arr.join(', '));
    }
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const removeUserFromField = (userName: string) => {
    const fieldIndex = data.findIndex((v: any) => v.field === 'assigned_users_input');
    if (fieldIndex >= 0) {
      const currentVal = data[fieldIndex].value || '';
      const arr = currentVal.split(',').map((a: string) => a.trim()).filter((a: string) => a && a !== userName);
      updateField('assigned_users_input', arr.join(', '));
    }
  };

  const updateField = (fieldName: string, value: any) => {
    const formEntry: any = [...data];
    const fieldIndex = formEntry.findIndex((v: any) => v.field === fieldName);
    if (fieldIndex >= 0) {
      formEntry[fieldIndex].value = value;
      setData(formEntry);
    }
  };

  const handleChange = (event: any) => {
    const formEntry: any = [...data];
    const fieldIndex = formEntry.findIndex((v: any) => v.field === event.target.id);
    if (fieldIndex < 0) return;
    formEntry[fieldIndex].value = event.target.value;
    setData(formEntry);
  };

  const handleFileUpload = async (event: any, fieldId: string) => {
    const file = event.target.files[0];
    if (!file) return;

    setShowLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('is_private', '0');
      formData.append('folder', 'Home');

      const uploadUrl =
        '/method/upload_file' +
        '?fieldname=attachment' +
        '&attached_to_field=attachment' +
        '&attached_to_doctype=Compliance%20Documents';

      const baseURL = localStorage.getItem('baseUrl') || process.env.REACT_APP_BASE_URL || 'https://admin.hyprism.com/api';
      const fetchUrl = uploadUrl.startsWith('http')
        ? uploadUrl
        : `${baseURL.replace(/\/+$/, '')}/${uploadUrl.replace(/^\/+/, '')}`;

      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
        },
        body: formData,
      });

      if (!response.ok && response.status !== 200) {
        throw new Error('Upload failed');
      }

      const resData = await response.json();
      const fileUrl = resData?.message?.file_url;

      if (fileUrl) {
        const formEntry: any = [...data];
        const fieldIndex = formEntry.findIndex((v: any) => v.field === fieldId);
        if (fieldIndex >= 0) {
          formEntry[fieldIndex].value = fileUrl;
          setData(formEntry);
        }
        present({ message: t('Documents.uploadSuccess', 'File uploaded successfully'), duration: 2000 });
      }
    } catch (error) {
      console.log('File upload error:', error);
      present({ message: t('Documents.uploadFailed', 'Failed to upload file'), duration: 2500, color: 'danger' });
    } finally {
      setShowLoading(false);
    }
  };

  const handleSelectChange = (event: any) => {
    const formEntry: any = [...data];
    const selectField = event.target.name.replaceAll('-', '_');
    const fieldIndex = formEntry.findIndex((v: any) => v.field === selectField);
    if (fieldIndex < 0) return;
    formEntry[fieldIndex].value = event.target.value;
    setData(formEntry);
  };

  const reset = () => {
    const nextData = [...data];
    nextData.forEach((entry: any) => {
      if (entry.type === 'multi-select') {
        entry.value = [];
      } else {
        entry.value = '';
      }
    });
    setData(nextData);
  };

  const submitDocument = async () => {
    const payload: any = {};
    data.forEach((entry: any) => {
      if (!entry.field || !entry.value) return;

      if (entry.field === 'assigned_projects_input') {
        const valueArr = Array.isArray(entry.value) ? entry.value : entry.value.split(',').map((p: string) => p.trim()).filter((p: string) => p);
        if (valueArr.length > 0) {
          payload.assigned_projects = valueArr.map((p: string) => ({ project: p }));
        }
      } else if (entry.field === 'assigned_assets_input') {
        const valueArr = Array.isArray(entry.value) ? entry.value : entry.value.split(',').map((p: string) => p.trim()).filter((p: string) => p);
        if (valueArr.length > 0) {
          payload.assigned_assets = valueArr.map((a: string) => ({ asset: a }));
        }
      } else if (entry.field === 'assigned_users_input') {
        const valueArr = Array.isArray(entry.value) ? entry.value : entry.value.split(',').map((p: string) => p.trim()).filter((p: string) => p);
        if (valueArr.length > 0) {
          payload.assigned_users = valueArr.map((u: string) => ({ user: u }));
        }
      } else {
        payload[entry.field] = entry.value;
      }
    });

    const userEmail = localStorage.getItem('email');
    if (userEmail && (!payload.assigned_users || payload.assigned_users.length === 0)) {
      payload.assigned_users = [{ user: userEmail }];
    }

    const config: any = {
      method: 'post',
      url: '/resource/Compliance%20Documents',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
      data: payload,
    };

    setShowLoading(true);
    try {
      await axios(config);
      present({
        message: t('Documents.createSuccess'),
        duration: 2000,
      });
      reset();
      router.push('/documents');
    } catch (error: any) {
      console.log(error?.response?.data || error);
      let message = t('Documents.createFail');
      try {
        const raw = JSON.parse(error?.response?.data?._server_messages);
        message = JSON.parse(raw[0]).message || message;
      } catch (e: any) {
        message = error?.response?.data?.message || message;
      }
      present({
        message,
        color: 'danger',
        duration: 2500,
      });
    } finally {
      setShowLoading(false);
    }
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    submitDocument();
  };

  return (
    <IonPage>
      <Header enableBackButton="goBack" title={t('Documents.create')} />
      <IonContent>
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          message={t('Utils.pleasewait')}
          duration={5000}
        />

        <form onSubmit={(e) => onSubmit(e)}>
          {data.map((formField: any, index: number) => {
            if (formField.title) {
              return (
                <p className="form-title" key={`${formField.title}-${index}`}>
                  {t(formField.title)}
                </p>
              );
            }

            if (formField.type === 'text' || formField.type === 'number') {
              return (
                <div className="input-container" key={`${formField.field}-${index}`}>
                  <label className="label">
                    {t(`Documents.${formField.field}`)}
                    {formField.required ? '*' : null}
                  </label>
                  <input
                    required={formField.required}
                    type={formField.type}
                    className="basic-input"
                    id={formField.field}
                    placeholder={t(`Documents.${formField.field}`)}
                    onChange={handleChange}
                    value={formField.value}
                  />
                </div>
              );
            }

            if (formField.type === 'select' || formField.type === 'multi-select') {
              return (
                <div className="input-container" key={`${formField.field}-${index}`}>
                  <label className="label">
                    {t(`Documents.${formField.field}`)}
                    {formField.required ? '*' : null}
                  </label>
                  <Select
                    className="custom-select"
                    style={{ fontSize: '12px' }}
                    required={formField.required}
                    name={formField.field}
                    onChange={handleSelectChange}
                    value={formField.value}
                    multiple={formField.type === 'multi-select'}
                    renderValue={(selected: any) => {
                      if (Array.isArray(selected)) {
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', overflow: 'hidden' }}>
                            {selected.map((value) => (
                              <div key={value} style={{ background: 'rgba(31, 87, 148, 0.1)', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', color: '#1F5794', border: '1px solid rgba(31, 87, 148, 0.2)' }}>
                                {value}
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return selected;
                    }}
                  >
                    {formField.options?.map((opt: string, indexSelect: number) => (
                      <MenuItem value={`${opt}`} key={`${formField.field}-${indexSelect}`}>
                        <section style={{ display: 'flex', height: '1rem' }}>
                          <p
                            style={{
                              color: 'rgba(31, 87, 148, 1)',
                              marginLeft: '0.2rem',
                              fontSize: '12px',
                              marginTop: '0px',
                            }}
                          >
                            {opt}
                          </p>
                        </section>
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              );
            }

            if (formField.type === 'date') {
              return (
                <div className="input-container" key={`${formField.field}-${index}`}>
                  <label className="label">
                    {t(`Documents.${formField.field}`)}
                    {formField.required ? '*' : null}
                  </label>
                  <input
                    required={formField.required}
                    className="date"
                    id={formField.field}
                    onChange={handleChange}
                    value={formField.value}
                    type="date"
                  />
                </div>
              );
            }

            if (formField.type === 'file') {
              return (
                <div className="input-container" key={`${formField.field}-${index}`}>
                  <label className="label">
                    {t(`Documents.${formField.field}`)}
                    {formField.required ? '*' : null}
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <input
                      required={formField.required && !formField.value}
                      type="file"
                      id={formField.field}
                      onChange={(e) => handleFileUpload(e, formField.field)}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    {formField.value && (
                      <span style={{ fontSize: '0.8rem', color: '#00a65a' }}>
                        {t('Documents.Uploaded')}: {formField.value}
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            if (formField.type === 'async-select') {
              const isCategory = formField.field === 'document_category';
              const isType = formField.field === 'document_type';
              const isProject = formField.field === 'assigned_projects_input';

              const searchVal = isCategory ? categorySearch : isType ? typeSearch : projectSearch;
              const handleSearchChange = isCategory ? handleCategorySearchChange : isType ? handleTypeSearchChange : handleProjectSearchChange;
              const options = isCategory ? categoryOptions : isType ? typeOptions : projectOptions;
              const showDropdown = isCategory ? showCategoryDropdown : isType ? showTypeDropdown : showProjectDropdown;
              const fetchFn = isCategory ? fetchCategories : isType ? fetchTypes : fetchProjects;
              const setShowDropdown = isCategory ? setShowCategoryDropdown : isType ? setShowTypeDropdown : setShowProjectDropdown;
              const scrollRef = isCategory ? categoryScrollRef : isType ? typeScrollRef : projectScrollRef;
              const handleScroll = isCategory ? handleCategoryScroll : isType ? handleTypeScroll : handleProjectScroll;
              const selectFn = isCategory ? selectCategory : isType ? selectType : selectProject;
              const loading = isCategory ? loadingCategory : isType ? loadingType : loadingProject;

              return (
                <div className="input-container" key={`${formField.field}-${index}`} style={{ position: 'relative' }}>
                  <label className="label">
                    {t(`Documents.${formField.field}`)}
                    {formField.required ? '*' : null}
                  </label>
                  <input
                    className="basic-input"
                    placeholder={t(`Documents.searchPlaceholder`)}
                    value={searchVal}
                    readOnly
                    onFocus={() => {
                      if (options.length === 0) fetchFn('', 0, false);
                      setShowDropdown(true);
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowDropdown(false), 200);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  {showDropdown && (
                    <div
                      ref={scrollRef}
                      onScroll={handleScroll}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '150px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        zIndex: 10,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                    >
                      {options.map((opt, i) => (
                        <div
                          key={i}
                          onClick={() => selectFn(opt)}
                          style={{ padding: '10px', borderBottom: '1px solid #eee', color: 'black', cursor: 'pointer' }}
                        >
                          {opt.project_name || opt.name}
                        </div>
                      ))}
                      {loading && <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>{t('Documents.Loading')}</div>}
                    </div>
                  )}
                </div>
              );
            }

            if (formField.type === 'async-multi-select') {
              const selectedItems = formField.value ? formField.value.split(',').map((s: string) => s.trim()).filter((s: string) => s) : [];

              const isAsset = formField.field === 'assigned_assets_input';
              const isUser = formField.field === 'assigned_users_input';

              const searchVal = isAsset ? assetSearch : userSearch;
              const handleSearchChange = isAsset ? handleAssetSearchChange : handleUserSearchChange;
              const options = isAsset ? assetOptions : userOptions;
              const showDropdown = isAsset ? showAssetDropdown : showUserDropdown;
              const fetchFn = isAsset ? fetchAssets : fetchUsers;
              const setShowDropdown = isAsset ? setShowAssetDropdown : setShowUserDropdown;
              const scrollRef = isAsset ? assetScrollRef : userScrollRef;
              const handleScroll = isAsset ? handleAssetScroll : handleUserScroll;
              const selectFn = isAsset ? selectAsset : selectUser;
              const removeFn = isAsset ? removeAssetFromField : removeUserFromField;
              const loading = isAsset ? loadingAsset : loadingUser;

              return (
                <div className="input-container" key={`${formField.field}-${index}`} style={{ position: 'relative' }}>
                  <label className="label">
                    {t(`Documents.${formField.field}`)}
                    {formField.required ? '*' : null}
                  </label>

                  {selectedItems.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                      {selectedItems.map((item: string, i: number) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#e0e0e0',
                            borderRadius: '16px',
                            padding: '4px 12px',
                            fontSize: '12px'
                          }}
                        >
                          <span style={{ marginRight: '8px' }}>{item}</span>
                          <span
                            onClick={() => removeFn(item)}
                            style={{ cursor: 'pointer', fontWeight: 'bold' }}
                          >
                            ×
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    className="basic-input"
                    placeholder={t(formField.placeholderKey) || t(`Documents.searchPlaceholder`)}
                    value={searchVal}
                    onChange={handleSearchChange}
                    onFocus={() => {
                      if (options.length === 0) fetchFn(searchVal, 0, false);
                      setShowDropdown(true);
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowDropdown(false), 200);
                    }}
                  />
                  {showDropdown && (
                    <div
                      ref={scrollRef}
                      onScroll={handleScroll}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '150px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        zIndex: 10,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                    >
                      {options.map((opt, i) => (
                        <div
                          key={i}
                          onClick={() => selectFn(opt)}
                          style={{ padding: '10px', borderBottom: '1px solid #eee', color: 'black', cursor: 'pointer' }}
                        >
                          {opt.full_name || opt.name}
                        </div>
                      ))}
                      {loading && <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>{t('Documents.Loading')}</div>}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
          <button className="submit-button" type="submit">
            {t('Documents.create')}
          </button>
        </form>
        <div style={{ marginBottom: '1rem' }}></div>
      </IonContent >
    </IonPage >
  );
};

export default CreateDocument;
